import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { trainingRequestsService } from '../../../services/trainingExchangeService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const STATUS_FLOW = ['requested', 'accepted', 'scheduled', 'completed'];

const STATUS_COLORS = {
  requested: 'bg-amber-100 text-amber-700',
  accepted: 'bg-blue-100 text-blue-700',
  scheduled: 'bg-violet-100 text-violet-700',
  completed: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
};

const URGENCY_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-red-100 text-red-700',
};

const TYPE_LABELS = {
  ecity_to_partner: { label: 'eCity → Partner', color: 'bg-emerald-100 text-emerald-700', icon: 'ArrowRight' },
  partner_to_ecity: { label: 'Partner → eCity', color: 'bg-violet-100 text-violet-700', icon: 'ArrowLeft' },
};

const NewRequestModal = ({ partners, onClose, onSuccess }) => {
  const [form, setForm] = useState({ request_type: 'ecity_to_partner', requester_name: '', requester_type: 'ecity', partner_id: '', topic: '', description: '', target_audience: '', preferred_format: 'online', preferred_date: '', urgency: 'normal' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => setForm(p => ({ ...p, [e?.target?.name]: e?.target?.value }));
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.topic?.trim() || !form?.requester_name?.trim()) { setError('Requester name and topic are required.'); return; }
    setSaving(true); setError(null);
    try { await trainingRequestsService?.create({ ...form, request_status: 'requested' }); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to submit request.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-orange-50 to-amber-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <Icon name="Plus" size={18} className="text-white" />
            </div>
            <h2 className="text-sm font-bold text-slate-900">New Training Request</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14 overflow-y-auto flex-1">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Request Direction</label>
            <select name="request_type" value={form?.request_type} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
              <option value="ecity_to_partner">eCity → Partner (eCity trains partner)</option>
              <option value="partner_to_ecity">Partner → eCity (Partner trains eCity staff)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Requester Name *</label><input name="requester_name" value={form?.requester_name} onChange={handleChange} placeholder="Your name" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20" required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Partner</label>
              <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                <option value="">Select partner...</option>
                {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
              </select>
            </div>
          </div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Training Topic *</label><input name="topic" value={form?.topic} onChange={handleChange} placeholder="e.g. Partner Portal Onboarding" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20" required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Description</label><textarea name="description" value={form?.description} onChange={handleChange} rows={2} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 resize-none" /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Target Audience</label><input name="target_audience" value={form?.target_audience} onChange={handleChange} placeholder="e.g. Partner Sales Team" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20" /></div>
          <div className="grid grid-cols-3 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Format</label>
              <select name="preferred_format" value={form?.preferred_format} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                {['online', 'in-person', 'hybrid']?.map(f => <option key={f} value={f}>{f?.charAt(0)?.toUpperCase() + f?.slice(1)}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Preferred Date</label><input type="date" name="preferred_date" value={form?.preferred_date} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Urgency</label>
              <select name="urgency" value={form?.urgency} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                {['low', 'normal', 'high']?.map(u => <option key={u} value={u}>{u?.charAt(0)?.toUpperCase() + u?.slice(1)}</option>)}
              </select></div>
          </div>
          <div className="flex gap-12 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-16 py-10 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl hover:opacity-90 disabled:opacity-60">
              {saving ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TrainingRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [addModal, setAddModal] = useState(false);
  const [partners, setPartners] = useState([]);
  const { showToast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setRequests(await trainingRequestsService?.getAll({ status: filterStatus, type: filterType })); }
    catch (e) { showToast?.('Failed to load requests', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus, filterType]);
  useEffect(() => { partnerService?.getAll?.()?.then(setPartners)?.catch(() => {}); }, []);

  const handleAdvanceStatus = async (req) => {
    const currentIdx = STATUS_FLOW?.indexOf(req?.request_status);
    if (currentIdx === -1 || currentIdx >= STATUS_FLOW?.length - 1) return;
    const nextStatus = STATUS_FLOW?.[currentIdx + 1];
    try { await trainingRequestsService?.update(req?.id, { request_status: nextStatus }); load(); showToast?.(`Status updated to ${nextStatus}`, 'success'); }
    catch (e) { showToast?.('Failed to update status', 'error'); }
  };

  const handleDecline = async (id) => {
    try { await trainingRequestsService?.update(id, { request_status: 'declined' }); load(); showToast?.('Request declined', 'success'); }
    catch (e) { showToast?.('Failed to decline', 'error'); }
  };

  const stats = {
    total: requests?.length,
    requested: requests?.filter(r => r?.request_status === 'requested')?.length,
    accepted: requests?.filter(r => r?.request_status === 'accepted')?.length,
    scheduled: requests?.filter(r => r?.request_status === 'scheduled')?.length,
    completed: requests?.filter(r => r?.request_status === 'completed')?.length,
  };

  return (
    <div>
      <div className="mb-20 p-16 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-100">
        <div className="flex items-center gap-12 mb-6">
          <Icon name="MessageSquarePlus" size={18} className="text-orange-600" />
          <h2 className="text-base font-bold text-slate-900">Training Requests</h2>
        </div>
        <p className="text-sm text-slate-600">Either side can request training from the other. Track status from request to completion.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-12 mb-24">
        {[
          { label: 'Requested', value: stats?.requested, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Accepted', value: stats?.accepted, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Scheduled', value: stats?.scheduled, color: 'text-violet-600', bg: 'bg-violet-50' },
          { label: 'Completed', value: stats?.completed, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ]?.map(s => (
          <div key={s?.label} className={`${s?.bg} rounded-2xl p-16 text-center`}>
            <p className={`text-2xl font-bold ${s?.color}`}>{s?.value}</p>
            <p className="text-xs text-slate-600 mt-4">{s?.label}</p>
          </div>
        ))}
      </div>

      {/* Filters + Add */}
      <div className="flex flex-col sm:flex-row gap-12 items-start sm:items-center justify-between mb-20">
        <div className="flex gap-12">
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            {['all', 'requested', 'accepted', 'scheduled', 'completed', 'declined']?.map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            <option value="all">All Types</option>
            <option value="ecity_to_partner">eCity → Partner</option>
            <option value="partner_to_ecity">Partner → eCity</option>
          </select>
        </div>
        <button onClick={() => setAddModal(true)} className="flex items-center gap-8 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl hover:opacity-90 transition-opacity">
          <Icon name="Plus" size={15} />New Request
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : requests?.length === 0 ? (
        <div className="text-center py-48 text-slate-500"><Icon name="MessageSquarePlus" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No training requests found</p></div>
      ) : (
        <div className="space-y-12">
          {requests?.map(req => {
            const typeInfo = TYPE_LABELS?.[req?.request_type];
            const currentIdx = STATUS_FLOW?.indexOf(req?.request_status);
            const canAdvance = currentIdx >= 0 && currentIdx < STATUS_FLOW?.length - 1 && req?.request_status !== 'declined';
            return (
              <div key={req?.id} className="bg-white rounded-2xl border border-slate-200 p-20 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-12 mb-12">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-8 mb-8 flex-wrap">
                      {typeInfo && (
                        <span className={`flex items-center gap-6 px-10 py-4 text-xs font-semibold rounded-full ${typeInfo?.color}`}>
                          <Icon name={typeInfo?.icon} size={12} />{typeInfo?.label}
                        </span>
                      )}
                      <span className={`px-10 py-4 text-xs font-semibold rounded-full ${STATUS_COLORS?.[req?.request_status] || 'bg-slate-100 text-slate-600'}`}>
                        {req?.request_status?.charAt(0)?.toUpperCase() + req?.request_status?.slice(1)}
                      </span>
                      <span className={`px-10 py-4 text-xs font-semibold rounded-full ${URGENCY_COLORS?.[req?.urgency] || 'bg-slate-100 text-slate-600'}`}>
                        {req?.urgency?.charAt(0)?.toUpperCase() + req?.urgency?.slice(1)} Priority
                      </span>
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm">{req?.topic}</h3>
                    <p className="text-xs text-slate-500 mt-4">{req?.requester_name} · {req?.partners?.brand_name || req?.partners?.legal_name || 'Internal'}</p>
                  </div>
                </div>
                {req?.description && <p className="text-xs text-slate-600 mb-12 line-clamp-2">{req?.description}</p>}
                <div className="flex flex-wrap items-center gap-16 text-xs text-slate-500 mb-14">
                  {req?.target_audience && <span className="flex items-center gap-6"><Icon name="Users" size={13} />{req?.target_audience}</span>}
                  <span className="flex items-center gap-6"><Icon name="Monitor" size={13} />{req?.preferred_format?.charAt(0)?.toUpperCase() + req?.preferred_format?.slice(1)}</span>
                  {req?.preferred_date && <span className="flex items-center gap-6"><Icon name="Calendar" size={13} />Preferred: {new Date(req?.preferred_date)?.toLocaleDateString()}</span>}
                  {req?.scheduled_date && <span className="flex items-center gap-6"><Icon name="CalendarCheck" size={13} />Scheduled: {new Date(req?.scheduled_date)?.toLocaleDateString()}</span>}
                </div>

                {/* Status Progress */}
                <div className="flex items-center gap-6 mb-14">
                  {STATUS_FLOW?.map((s, i) => (
                    <React.Fragment key={s}>
                      <div className={`flex items-center gap-4 text-xs font-medium ${STATUS_FLOW?.indexOf(req?.request_status) >= i ? 'text-orange-600' : 'text-slate-400'}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${STATUS_FLOW?.indexOf(req?.request_status) >= i ? 'bg-orange-500' : 'bg-slate-200'}`}>
                          {STATUS_FLOW?.indexOf(req?.request_status) > i ? <Icon name="Check" size={10} className="text-white" /> : <span className="text-white text-xs">{i + 1}</span>}
                        </div>
                        <span className="hidden sm:inline">{s?.charAt(0)?.toUpperCase() + s?.slice(1)}</span>
                      </div>
                      {i < STATUS_FLOW?.length - 1 && <div className={`flex-1 h-px ${STATUS_FLOW?.indexOf(req?.request_status) > i ? 'bg-orange-300' : 'bg-slate-200'}`} />}
                    </React.Fragment>
                  ))}
                </div>

                <div className="flex gap-10">
                  {canAdvance && (
                    <button onClick={() => handleAdvanceStatus(req)} className="flex items-center gap-8 px-14 py-9 text-xs font-semibold text-orange-700 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
                      <Icon name="ArrowRight" size={13} />Advance to {STATUS_FLOW?.[currentIdx + 1]?.charAt(0)?.toUpperCase() + STATUS_FLOW?.[currentIdx + 1]?.slice(1)}
                    </button>
                  )}
                  {req?.request_status === 'requested' && (
                    <button onClick={() => handleDecline(req?.id)} className="flex items-center gap-8 px-14 py-9 text-xs font-semibold text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors">
                      <Icon name="X" size={13} />Decline
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {addModal && <NewRequestModal partners={partners} onClose={() => setAddModal(false)} onSuccess={() => { setAddModal(false); load(); showToast?.('Training request submitted', 'success'); }} />}
    </div>
  );
};

export default TrainingRequests;
